import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { COLOR_ARRAY } from "src/config/styleConfig";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { monitoringClient } from "src/services/requestClient";
import { numberWithCommas, toFixed } from "src/services/utils";
import MarketEventTable from "./MarketEventTable";

const dateOption = [
  { label: "1D", number: 24, interval: 1 },
  { label: "1W", number: 168, interval: 1 },
  { label: "1M", number: 720, interval: 1 },
  { label: "3M", number: 540, interval: 4 },
];

async function getEvent(pool, timestamp) {
  try {
    let data = await monitoringClient.get(
      `/transactions/event-data?lending=${pool}&to=${timestamp}&from=${timestamp - 3600}`
    );
    if (data.data.length < 1) {
      data = await monitoringClient.get(`/transactions/event-data?limit=5&lending=${pool}&to=${timestamp}`);
    }
    return data.data;
  } catch (error) {
    console.error(error);
  }
}

const getOption = (pool, setTableData) => {
  return {
    chart: {
      height: 500,
      events: {
        click: async function (event) {
          const timestamp = toFixed(event.xAxis[0].value / 1000);
          const data = await getEvent(pool, timestamp);
          setTableData(data);
        },
      },
      zoomType: "x",
      panning: {
        enabled: true,
        type: "x",
      },
      panKey: "shift",
    },
    yAxis: {
      labels: {
        enabled: true,
      },
    },
    xAxis: {
      gridLineWidth: 1,
      type: "datetime",
      labels: {
        enabled: true,
      },
    },
    legend: {
      enabled: true,
    },
    ...beautifulTooltip((value) => `$${numberWithCommas(toFixed(value, 2))}`),
    plotOptions: {
      series: {
        cursor: "pointer",
        events: {
          click: function (event) {
            const timestamp = toFixed(event.point.category / 1000);
            getEvent(pool, timestamp)
              .then((data) => {
                setTableData(data);
              })
              .catch((error) => {
                console.error(error);
              });
          },
        },
      },
    },
  };
};

export default function MarketOverview() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  const { fetchMarketOverview } = useAnalysisOverviewSelector((state) => state.fetch);
  const { marketOverviewLoading } = useAnalysisOverviewSelector((state) => state.loading);
  const marketOverview = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.marketOverview, pool);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (marketOverview?.active === undefined) fetchMarketOverview(pool, { number: 720, interval: 1 }, { active: 2 });
    else fetchMarketOverview(pool, marketOverview?.metadata, { active: marketOverview?.active });
  }, [marketOverview?.active, pool]);

  const realTableData = useMemo(() => {
    return tableData.map((element) => {
      let color = null;
      if (element.event_type === "DEPOSIT") color = COLOR_ARRAY[0];
      else if (element.event_type === "BORROW") color = COLOR_ARRAY[1];
      else if (element.event_type === "WITHDRAW") color = COLOR_ARRAY[2];
      else if (element.event_type === "REPAY") color = COLOR_ARRAY[3];
      return {
        color: color,
        type: element.event_type,
        time: element.timestamp,
        amount: element.amount,
        user: element.user,
        symbol: element.symbol,
        token: element.token,
        transaction: element.tx_hash,
      };
    });
  }, [tableData]);

  const { series } = useMemo(() => {
    if (marketOverview && !marketOverviewLoading) {
      return {
        series: [
          { name: "Supply", data: [].concat(marketOverview.supply) },
          { name: "Borrow", data: [].concat(marketOverview.borrow) },
          { name: "Total Value Lock", data: [].concat(marketOverview.tvl) },
        ],
        isLoading: false,
      };
    }
    return {
      series: [
        { name: "Supply", data: [] },
        { name: "Borrow", data: [] },
        { name: "Total Value Lock", data: [] },
      ],
      isLoading: true,
    };
  }, [marketOverview, marketOverviewLoading]);

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "overview", link: `/overview-analysis/${pool}`, name: "market overview" }}
      selectLink="/overview-analysis/market-overview"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the Total Supply, Total Borrow, and Total Value Locked of the selected lending pool. Those
            values are recorded every hour. The spots in the chart indicate unusual changes that exceed 50,000 US
            dollars. Users can click on a spot to see the respective transaction report represented in a table.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        <Box mb={2} display="flex" justifyContent="flex-end">
          <DateGroupButton
            fullWidth={match}
            defaultActive={marketOverview?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchMarketOverview(pool, { from, to }, { active });
              },
            }}
            config={dateOption.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchMarketOverview(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        <LineChart series={series} option={getOption(pool, setTableData)} />
      </Paper>
      <MarketEventTable data={realTableData.sort((a, b) => b.amount - a.amount)} />
    </DetailMonitoringWrapper>
  );
}
